<template>
	<div class="container_section p-5">
		<div class="table_header is-flex is-justify-content-space-between is-align-items-center mb-4">
			<div class="is-flex is-align-items-center">
				<Ecs class="mr-2" style="width: 20px; height: 20px;" />
				<span class="tittle_section">{{ title }}</span>
			</div>
			<div class="table_actions is-flex">
				<button class="action_btn mr-2">
					<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
					Comparación
				</button>
				<button class="action_btn mr-2">
					<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
					Ver Gráfico
				</button>
				<div style="min-width: 140px;">
					<SelectComponent :value="selectedPeriod" theme="report_theme"
						:options="periodOptions"
						@input="$emit('change-period', $event)" />
				</div>
			</div>
		</div>

		<section>
			<div v-if="isLoading" class="loading_state py-6 has-text-centered">
				<p>Cargando datos...</p>
			</div>

			<div v-else-if="error" class="error_state py-6 has-text-centered">
				<p class="has-text-danger">{{ error }}</p>
				<button class="action_btn mt-3" @click="$emit('retry')">Reintentar</button>
			</div>

			<div v-else-if="monthHeaders.length === 0" class="empty_state py-6 has-text-centered">
				<p>{{ emptyMessage }}</p>
			</div>

			<div v-else class="table_container" ref="mainTable">
				<table class="custom_table">
					<colgroup>
						<col style="width: 240px;" />
						<col style="width: 160px;" v-for="header in monthHeaders" :key="header" />
					</colgroup>
					<thead>
						<tr>
							<th scope="col" class="sticky-column header_label nobreak"></th>
							<th scope="col" class="has-text-right nobreak is-capitalized"
								v-for="header in monthHeaders" :key="header">
								{{ header }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in visibleRows" :key="row.label"
							:class="['theme_row_white', { 'row-level-2': row.level === 2, 'row-level-3': row.level === 3 }]">
							<th scope="row"
								:class="['sticky-column nobreak theme_row_white is-capitalized', row.level === 3 ? 'pl-5' : '']"
								@click="row.level === 2 ? $emit('toggle-l2', row.label) : null"
								:style="row.level === 2 ? 'cursor: pointer;' : ''">
								<div class="is-flex is-align-items-center is-justify-content-space-between">
									<div class="is-flex is-align-items-center label_cell">
										<span v-if="row.level === 3" class="indent-symbol mr-1">↳</span>
										<span v-if="row.label.length > 30"
											class="has-tooltip-right label_text"
											:data-tooltip="row.label">
											{{ row.label.substring(0, 30) + '...' }}
										</span>
										<span v-else class="label_text">
											{{ row.label }}
										</span>
									</div>
									<ArrowDown v-if="row.level === 2" class="expand-icon"
										:class="{ 'rotate-icon': expandedL2Rows.includes(row.label) }" />
								</div>
							</th>
							<td v-for="(val, idx) in row.values" :key="idx"
								class="has-text-right nobreak theme_row_white">
								{{ parseToMoney(val) }}
							</td>
						</tr>

						<tr class="theme_row_highlight"
							:style="{ '--row-sticky-bottom': totalRowStickyBottom }">
							<th scope="row" class="sticky-column nobreak theme_row_highlight">
								{{ totalRow.label }}
							</th>
							<td v-for="(val, idx) in totalRow.values" :key="'total-' + idx"
								class="has-text-right nobreak theme_row_highlight">
								{{ parseToMoney(val) }}
							</td>
						</tr>

						<tr v-for="(row, ridx) in level2RatioRows" :key="'l2r-' + row.label"
							class="theme_row_secondary"
							:style="{ '--row-sticky-bottom': l2RatioStickyBottom(ridx) }">
							<th scope="row" class="sticky-column nobreak theme_row_secondary">
								{{ row.label }}
							</th>
							<td v-for="(val, idx) in row.values" :key="'l2r-' + ridx + '-' + idx"
								class="has-text-right nobreak theme_row_secondary">
								{{ formatPercent(val) }}
							</td>
						</tr>

						<tr class="theme_row_secondary">
							<th scope="row" class="sticky-column nobreak theme_row_secondary">
								{{ ratioRow.label }}
							</th>
							<td v-for="(val, idx) in ratioRow.values" :key="'eff-' + idx"
								class="has-text-right nobreak theme_row_secondary">
								{{ formatPercent(val) }}
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</section>
	</div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import { formatPercent } from "@/views/financialReports/utils/periodFormatting";
import SelectComponent from "@/components/classification/SelectComponent";
import Ecs from "@/components/svg/Ecs.vue";
import ArrowDown from "@/components/svg/ArrowDown.vue";
import ChartLine from "@/components/svg/ChartLine.vue";

export default {
	name: "ExpensesEvolutionTable",

	components: { SelectComponent, Ecs, ArrowDown, ChartLine },

	props: {
		monthHeaders: { type: Array, required: true },
		visibleRows: { type: Array, required: true },
		totalRow: { type: Object, required: true },
		ratioRow: { type: Object, required: true },
		// Filas opcionales de ratio por level_2 (Costos). Se renderizan entre
		// totalRow y ratioRow, todas sticky-bottom apiladas. Vacío en Gastos.
		level2RatioRows: { type: Array, default: () => [] },
		expandedL2Rows: { type: Array, default: () => [] },
		isLoading: { type: Boolean, default: false },
		error: { type: String, default: "" },
		selectedPeriod: { type: String, required: true },
		periodOptions: { type: Array, required: true },
		// Permite reusar la tabla en Costos sin duplicar markup. Default mantiene
		// el comportamiento existente para no romper GastosTab.
		title: { type: String, default: "Evolución de Gastos & Eficiencia" },
		emptyMessage: { type: String, default: "No hay datos de gastos para el período seleccionado." },
	},

	computed: {
		// Alto de fila debe coincidir con el `height: 44px` de th/td en CSS.
		// Total se apila por encima de las N filas L2-ratio + la ratioRow base.
		totalRowStickyBottom() {
			return ((this.level2RatioRows.length + 1) * 44) + "px";
		},
	},

	methods: {
		parseToMoney,
		formatPercent,
		// Filas L2-ratio: la primera (idx 0) es la más alejada del fondo;
		// la última (idx L-1) queda inmediatamente sobre ratioRow.
		l2RatioStickyBottom(idx) {
			return ((this.level2RatioRows.length - idx) * 44) + "px";
		},
		scrollToRight() {
			this.$nextTick(() => {
				const table = this.$refs.mainTable;
				if (table) table.scrollLeft = table.scrollWidth;
			});
		},
	},

	watch: {
		monthHeaders() { this.scrollToRight(); },
	},
};
</script>

<style scoped lang="scss">
.container_section {
	background: white;
	border-radius: 16px;
	border: 1px solid var(--report-border);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tittle_section {
	font-size: 18px;
	font-weight: 600;
}

.action_btn {
	background: white;
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 6px 12px;
	font-size: 14px;
	font-weight: 500;
	display: flex;
	align-items: center;
	cursor: pointer;

	&:hover { background: var(--report-surface); }
}

.table_container {
	// Scroll en ambos ejes: x para periodos, y para listas largas.
	overflow: auto;
	border: 1px solid var(--report-border);
	border-radius: 12px;
	// Alto máx ≈ thead (44) + 11 filas datos + totales (44) + ratio (44) = 14×44 = 616px.
	// max-height (no height) → si hay menos filas, la tabla se ajusta.
	max-height: calc(44px * 14);
	// Aísla el subárbol — repaints/reflows no se propagan.
	contain: layout paint;
}

.custom_table {
	min-width: 100%;
	border-collapse: separate;
	border-spacing: 0;

	th, td {
		// height fijo para que el cálculo de `bottom` de la fila superior
		// del bloque sticky-bottom (ratio + totales) coincida con el alto
		// real de la fila de abajo.
		height: 44px;
		padding: 12px 16px;
		border-bottom: 1px solid var(--report-divider-soft);
		font-size: 13px;
	}

	thead th {
		background: var(--report-surface);
		color: var(--report-text-muted);
		font-weight: 700;
		text-align: right;
		// Sticky-top: encabezado siempre visible al scrollear verticalmente.
		position: sticky;
		top: 0;
		z-index: 3;
	}

	.sticky-column {
		position: sticky;
		left: 0;
		z-index: 2;
		border-right: 1px solid var(--report-border);
	}

	.theme_row_white { background: var(--report-bg); }

	.theme_row_highlight {
		// Tokenizado para que cada data-theme (gastos/costos/...) defina su
		// propio highlight. Fallback al amber original de Gastos para no romper
		// los temas sin override.
		background: var(--report-highlight-bg, #fef3c7);
		font-weight: 700;

		th, td {
			border-bottom: 1px solid var(--report-highlight-border, #fbbf24);
			position: sticky;
			// Overridable por inline-style del <tr> en Costos, donde el total
			// debe apilarse encima de N filas de ratio L2.
			bottom: var(--row-sticky-bottom, 44px);
			z-index: 3;
		}
	}

	.theme_row_secondary {
		background: var(--report-surface);
		color: var(--report-text-muted);
		font-weight: 600;

		th, td {
			// Sticky-bottom: anclado al borde inferior del scroll-container.
			// Overridable por inline-style del <tr> para apilar varias filas
			// secondary (ratio total + ratios L2 en Costos).
			position: sticky;
			bottom: var(--row-sticky-bottom, 0px);
			z-index: 3;
		}
	}

	// Esquinas sticky-en-dos-ejes (top-left del thead y bottom-left de las
	// dos filas pinneadas abajo): suben a z-index 4 para pintar por encima
	// de los stickies de un solo eje.
	thead .sticky-column,
	.theme_row_highlight .sticky-column,
	.theme_row_secondary .sticky-column {
		z-index: 4;
	}

	.row-level-2 {
		font-weight: 700;
		background: #fcfcfc;
	}

	.row-level-3 {
		color: var(--report-text-muted-strong);

		th, td {
			font-size: 12.5px !important;
			color: var(--report-text-muted) !important;
		}
	}

	.pl-5 { padding-left: 32px !important; }

	.indent-symbol {
		color: var(--report-text-muted-soft);
		font-weight: 400;
	}

	.expand-icon {
		width: 14px;
		height: 14px;
		transition: transform 0.3s ease;
		filter: brightness(0) saturate(100%) invert(60%) sepia(5%) saturate(100%) hue-rotate(180deg);
	}

	.rotate-icon { transform: rotate(180deg); }
}
</style>
